import { useQueryClient } from "@tanstack/react-query";

import type { Business, BusinessProfileResponse } from "@/schemas";

import {
  getBusiness,
  getBusinessProfile,
  updateBusinessProfile,
  uploadBusinessImage,
  getStripeConnectStatus,
} from "../api/businessApi";
import { QueryKeys } from "../queryKeys";
import { useCustomMutation, useCustomQuery } from "../queryUtils";
import { notify } from "@/utils";

export function useGetBusinessQuery(userId: string, options?: { enabled?: boolean }) {
  return useCustomQuery<Business, Error>(
    ["business", userId],
    () => getBusiness(userId),
    {
      ...options,
    },
  );
}

export function useGetBusinessProfileQuery(options?: { enabled?: boolean }) {
  return useCustomQuery<BusinessProfileResponse, Error>(
    [QueryKeys.BUSINESS_PROFILE],
    getBusinessProfile,
    {
      ...options,
    },
  );
}

export function useUpdateBusinessProfileMutation() {
  const queryClient = useQueryClient();
  return useCustomMutation<BusinessProfileResponse, Error, Partial<Business>>(
    updateBusinessProfile,
    {
      onError: (error: Error) => {
        console.error("Update business profile failed:", error);
        notify.error("Failed to update business profile. Please try again.");
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.BUSINESS_PROFILE],
        });
      },
    },
  );
}

export function useUploadBusinessImageMutation() {
  const queryClient = useQueryClient();
  return useCustomMutation<unknown, Error, File>(
    uploadBusinessImage,
    (/* file */) => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.BUSINESS_PROFILE] });
    },
  );
}

export function useGetStripeConnectStatusQuery(businessId: string, options?: { enabled?: boolean }) {
  return useCustomQuery(
    ["stripe-connect-status", businessId],
    () => getStripeConnectStatus(businessId),
    {
      ...options,
    },
  );
}
